import fs from 'node:fs';
import path from 'node:path';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { INSTALL_SKILLS_YIELD_EVERY } from './constants.js';
import { isValidRemoteSkillInstallItem } from './is-valid-install-item.js';
import type { InstallRemoteSkillsResult, RemoteSkillInstallItem } from './types.js';

async function writeInstallFileAsync(
  skillDir: string,
  f: RemoteSkillInstallItem['files'][number],
  filesWritten: { count: number },
): Promise<void> {
  if (typeof f.path !== 'string' || (!f.text && !f.base64)) return;
  if (++filesWritten.count % INSTALL_SKILLS_YIELD_EVERY === 0) {
    await yieldToEventLoop();
  }
  const dest = path.join(skillDir, f.path);
  await fs.promises.mkdir(path.dirname(dest), { recursive: true });
  if (f.text !== undefined) {
    await fs.promises.writeFile(dest, f.text, 'utf8');
  } else if (f.base64) {
    await fs.promises.writeFile(dest, Buffer.from(f.base64, 'base64'));
  }
}

/**
 * Like {@link installRemoteSkills}, yielding between files so the bridge event loop can run.
 */
export async function installRemoteSkillsAsync(
  cwd: string,
  targetDir: string,
  items: unknown[],
): Promise<InstallRemoteSkillsResult> {
  const installed: Array<{ sourceId: string; skillName: string; versionHash: string }> = [];
  if (!Array.isArray(items)) {
    return { success: false, error: 'Invalid items' };
  }
  const filesWritten = { count: 0 };
  try {
    for (const item of items) {
      if (!isValidRemoteSkillInstallItem(item)) continue;
      const skillDir = path.join(cwd, targetDir, item.skillName);
      for (const f of item.files) {
        await writeInstallFileAsync(skillDir, f, filesWritten);
      }
      installed.push({
        sourceId: item.sourceId,
        skillName: item.skillName,
        versionHash: item.versionHash,
      });
    }
    return { success: true, installed };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}
